import { useCallback, useEffect, useRef, useState } from 'react';
import './SearchBar.css';

interface SearchBarProps {
  value: string;
  onChange: (value: string) => void;
  onSearch: (query: string) => void;
  error?: string | null;
}

const DEBOUNCE_MS = 300;

export function SearchBar({ value, onChange, onSearch, error }: SearchBarProps) {
  const [localValue, setLocalValue] = useState(value);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Sync external value changes
  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = e.target.value;
      setLocalValue(val);
      onChange(val);

      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }

      timerRef.current = setTimeout(() => {
        onSearch(val);
      }, DEBOUNCE_MS);
    },
    [onChange, onSearch]
  );

  const handleClear = useCallback(() => {
    setLocalValue('');
    onChange('');
    onSearch('');
    inputRef.current?.focus();
  }, [onChange, onSearch]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        if (timerRef.current) {
          clearTimeout(timerRef.current);
        }
        onSearch(localValue);
      }
    },
    [localValue, onSearch]
  );

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  return (
    <div className="search-bar">
      <div className={`search-bar__input-wrapper${error ? ' search-bar__input-wrapper--error' : ''}`}>
        <svg
          className="search-bar__icon"
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <circle cx="11" cy="11" r="7" />
          <path d="M21 21l-4.35-4.35" />
        </svg>
        <input
          ref={inputRef}
          type="search"
          className="search-bar__input"
          placeholder="Search"
          value={localValue}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          aria-label="Search"
          aria-invalid={!!error}
          aria-describedby={error ? 'search-bar-error' : undefined}
        />
        {localValue && (
          <button
            className="search-bar__clear"
            onClick={handleClear}
            aria-label="Clear search"
            type="button"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M18 6L6 18" />
              <path d="M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>
      {error && (
        <p className="search-bar__error" id="search-bar-error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
